import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { getTaskStatus, isTaskGone, TASK_GONE_MESSAGE } from '../api/images'
import {
	PENDING_GENERATION_KEY,
	isPendingExpired,
	pollDelayMs,
} from '../pages/generate/utils/constants'
import { pendingOwner } from '../utils/forgetPendingGenerations'
import { isOtherUser, sessionUserId } from '../utils/sessionUser'

interface PendingTask {
	taskId: string
	modelId: string
	modelName?: string
	startedAt: number
}

export type GlobalGenerationStatus = 'idle' | 'running' | 'done' | 'error'

const UNSEEN_KEY = 'gen_unseen_result'

interface GlobalGenerationCtx {
	status: GlobalGenerationStatus
	modelName: string | null
	errorMessage: string | null
	hasUnseenResult: boolean
	setTask: (taskId: string, modelId: string, modelName: string, skipPoll?: boolean) => void
	/** Пометить «идёт генерация» БЕЗ задачи для опроса — синхронный путь. */
	setRunning: (modelName: string) => void
	setDone: () => void
	setFailed: (message?: string) => void
	clearDone: () => void
	markResultSeen: () => void
}

const Ctx = createContext<GlobalGenerationCtx>({
	status: 'idle',
	modelName: null,
	errorMessage: null,
	hasUnseenResult: false,
	setTask: () => {},
	setRunning: () => {},
	setDone: () => {},
	setFailed: () => {},
	clearDone: () => {},
	markResultSeen: () => {},
})

export function GlobalGenerationProvider({ children }: { children: React.ReactNode }) {
	const [status, setStatus] = useState<GlobalGenerationStatus>('idle')
	const [modelName, setModelName] = useState<string | null>(null)
	const [errorMessage, setErrorMessage] = useState<string | null>(null)
	const [hasUnseenResult, setHasUnseenResult] = useState(
		() => localStorage.getItem(UNSEEN_KEY) === '1',
	)
	const currentTaskId = useRef<string | null>(null)
	const abortRef = useRef(false)

	// Опрос без потолка: генерацию закрывает ответ сервера (план «Ждём ответа нейросети», Ф2), остановить его могут
	// следующая генерация и «Продолжить в фоне» (abortRef). Без входа сервер не спрашиваем — ждём входа (ответ 401
	// уводит на /login, и опрос перезагружал бы страницу входа по кругу).
	const runPoll = useCallback(async (task: PendingTask) => {
		abortRef.current = false
		let owner = pendingOwner() || sessionUserId()

		for (;;) {
			await new Promise((r) => setTimeout(r, pollDelayMs(Date.now() - task.startedAt)))
			if (abortRef.current) return
			const current = sessionUserId()
			if (!current && current !== '') continue
			if (isOtherUser(owner, current)) {
				// Вошёл другой человек: генерация не его — не ждём и не показываем «идёт генерация» (ревью 27.09).
				currentTaskId.current = null
				setModelName(null)
				setErrorMessage(null)
				setStatus('idle')
				return
			}
			owner = owner || current

			try {
				const s = await getTaskStatus(task.taskId)
				if (abortRef.current) return

				if (s.status === 'completed') {
					setStatus('done')
					setHasUnseenResult(true)
					localStorage.setItem(UNSEEN_KEY, '1')
					currentTaskId.current = null
					return
				}
				if (s.status === 'failed') {
					setErrorMessage(s.error_message_ru || s.error_message || null)
					setStatus('error')
					localStorage.removeItem(PENDING_GENERATION_KEY)
					currentTaskId.current = null
					return
				}
			} catch (e) {
				if (abortRef.current) return
				if (isTaskGone(e)) {
					setErrorMessage(TASK_GONE_MESSAGE)
					setStatus('error')
					localStorage.removeItem(PENDING_GENERATION_KEY)
					currentTaskId.current = null
					return
				}
				// network hiccup — keep polling
			}
		}
	}, [])

	const setTask = useCallback(
		(taskId: string, _modelId: string, name: string, skipPoll?: boolean) => {
			if (currentTaskId.current === taskId) return
			abortRef.current = true // stop any previous poll
			currentTaskId.current = taskId
			setModelName(name)
			setStatus('running')
			// skipPoll = true когда вызывающий уже сам поллит (useGenerationFlow)
			if (!skipPoll) {
				const task: PendingTask = { taskId, modelId: _modelId, modelName: name, startedAt: Date.now() }
				setTimeout(() => runPoll(task), 60)
			}
		},
		[runPoll],
	)

	/**
	 * Генерация пошла, но опрашивать нечего: картиночные модели отвечают ОДНИМ
	 * запросом, taskId не появляется вовсе — и статус оставался `idle` всё
	 * время работы. Из-за этого после перехода на другую страницу и обратно
	 * инструмент не знал, что генерация идёт, и рисовал пустую зону вместо
	 * прелоадера (владелец, 21.09: «кликнул на генерирующуюся пилюлю — в
	 * виджете не показывает генерацию»). Статус должен означать «идёт
	 * генерация», а не «есть задача для опроса».
	 */
	const setRunning = useCallback((name: string) => {
		abortRef.current = true
		currentTaskId.current = null
		setModelName(name)
		setErrorMessage(null)
		setStatus('running')
	}, [])

	const setDone = useCallback(() => {
		setStatus('done')
		setHasUnseenResult(true)
		localStorage.setItem(UNSEEN_KEY, '1')
		currentTaskId.current = null
	}, [])

	const setFailed = useCallback((message?: string) => {
		setErrorMessage(message ?? null)
		setStatus('error')
		localStorage.removeItem(PENDING_GENERATION_KEY)
		currentTaskId.current = null
	}, [])

	const clearDone = useCallback(() => {
		abortRef.current = true
		currentTaskId.current = null
		setStatus('idle')
		setModelName(null)
	}, [])

	const markResultSeen = useCallback(() => {
		setHasUnseenResult(false)
		localStorage.removeItem(UNSEEN_KEY)
	}, [])

	// Resume any pending task from localStorage on mount
	useEffect(() => {
		const saved = localStorage.getItem(PENDING_GENERATION_KEY)
		if (!saved) return
		try {
			const task: PendingTask = JSON.parse(saved)
			if (isPendingExpired(task)) {
				localStorage.removeItem(PENDING_GENERATION_KEY)
				return
			}
			// В этот же ключ useGenerationFlow пишет snapshot ещё ДО ответа
			// generate-async — taskId дописывается позже (updatePendingTaskId).
			// Реалоад в этом окне или синхронная генерация оставляют запись без
			// taskId: поллить нечем (иначе цикл GET /tasks/undefined). Ключ не
			// трогаем — он нужен BotsWidgetContext, TTL-ветка выше его почистит.
			if (!task.taskId) return
			currentTaskId.current = task.taskId
			setModelName(task.modelName || task.modelId)
			setStatus('running')
			runPoll(task)
		} catch {
			localStorage.removeItem(PENDING_GENERATION_KEY)
		}
	}, [runPoll])

	return (
		<Ctx.Provider value={{ status, modelName, errorMessage, hasUnseenResult, setTask, setRunning, setDone, setFailed, clearDone, markResultSeen }}>
			{children}
		</Ctx.Provider>
	)
}

// eslint-disable-next-line react-refresh/only-export-components
export function useGlobalGeneration() {
	return useContext(Ctx)
}
